import * as Sentry from '@sentry/react';
import api from 'api';
import store from 'store';

import { State } from 'store';
import IAsync from 'types/IAsync';
import IPaginatedResponse from 'types/IPaginatedResponse';
import IRunEntry from 'types/IRunEntry';

export type DashboardSortKey = 'updated_on' | 'created_on' | 'name' | 'running_tasks';
export type DashboardSortOrder = 'asc' | 'desc';

export interface IDashboardRunsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  projectIds?: string[];
  userId?: string;
  hasRunningTasks?: boolean;
  sort?: DashboardSortKey;
  order?: DashboardSortOrder;
}

function setRunsAsync(runsAsync: IAsync<IPaginatedResponse<IRunEntry>>) {
  store.setState((state: State) => ({
    dashboard: { ...state.dashboard, runsAsync },
  }));
}

function buildUrl(params: IDashboardRunsParams): string {
  const searchParams = new URLSearchParams();
  if (params.page) searchParams.set('page', String(params.page));
  if (params.pageSize) searchParams.set('page_size', String(params.pageSize));
  if (params.search) searchParams.set('search', params.search);
  for (const projectId of params.projectIds ?? []) searchParams.append('project_id', projectId);
  if (params.userId) searchParams.set('user_id', params.userId);
  if (params.hasRunningTasks) searchParams.set('has_running_tasks', 'true');
  if (params.sort) searchParams.set('sort', params.sort);
  if (params.order) searchParams.set('order', params.order);

  const qs = searchParams.toString();
  return `dashboard/workflows${qs ? `?${qs}` : ''}`;
}

// Identifies the most recently requested query so that stale responses (e.g. an
// in-flight fetch whose filters the user has since changed) can be discarded.
let latestUrl: string | null = null;

export default function fetchDashboardRuns(params: IDashboardRunsParams = {}) {
  const url = buildUrl(params);
  const runsAsync = store.getState().dashboard.runsAsync;

  // Skip only when an identical query is already in flight; a changed query must
  // supersede the pending one rather than be dropped.
  if ((runsAsync.status === 'PENDING' || runsAsync.status === 'REFRESH') && url === latestUrl) {
    return;
  }
  latestUrl = url;

  if (runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH') {
    setRunsAsync({ status: 'REFRESH', data: runsAsync.data });
  } else {
    setRunsAsync({ status: 'PENDING' });
  }

  api
    .get(url)
    .json()
    .then(
      (data) => {
        if (url !== latestUrl) return; // superseded by a newer query
        setRunsAsync({
          status: 'SUCCESS',
          data: data as IPaginatedResponse<IRunEntry>,
        });
      },
      (error) => {
        if (url !== latestUrl) return; // superseded by a newer query
        Sentry.captureException(error);
        setRunsAsync({ status: 'ERROR' });
      },
    );
}
